import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {OverlayPanel} from "primereact/overlaypanel";
import {ProgressSpinner} from "primereact/progressspinner";
import {RadioButton} from "primereact/radiobutton";
import {SelectButton} from "primereact/selectbutton";
import {Tag} from "primereact/tag";
import Avatar from "../../system/components/avatar";
import AuditLog from "../../models/audit-log/audit-log";
import {useTranslate} from "../../system/useTranslate";
import {dateToPersian} from "../../system/utils";
import {formatAuditValue, getAuditChanges} from "./audit-changes";

const DEFAULT_TIME_RANGE = "week";
const auditLogApi = new AuditLog();
const AUDITED_MODEL_META = new Map([
    ["api::task.task", {category: "task", labelKey: "events-center.model.task"}],
    ["api::project.project", {category: "project", labelKey: "events-center.model.project"}],
    ["api::deliverable.deliverable", {category: "milestone", labelKey: "events-center.model.milestone"}],
    ["api::goal.goal", {category: "goal", labelKey: "events-center.model.goal"}],
    ["api::team.team", {category: "team", labelKey: "events-center.model.team"}],
    ["api::workspace.workspace", {category: "workspace", labelKey: "events-center.model.workspace"}],
    ["api::report.report", {category: "report", labelKey: "events-center.model.report"}],
]);
const CATEGORY_KEYS = new Set(["project", "milestone", "goal", "task", "team", "workspace", "report", "system"]);
const CHANGE_KEYS = new Set(["create", "update", "delete"]);
const SOURCE_KEYS = new Set(["user", "ai", "system"]);
const valueKey = (item) => item?.documentId ?? item?.id ?? null;
const actorName = (actor, fallback) => actor?.fullname
    || [actor?.firstname, actor?.lastname].filter(Boolean).join(" ")
    || actor?.username
    || fallback;
const relationTitle = (item) => item?.title || item?.name || item?.label || "";
const optionFromEntity = (item) => ({label: relationTitle(item), value: String(valueKey(item))});
const isImportant = (item) => ["important", "critical", "high", "urgent", "warning", "danger"]
    .includes(String(item?.severity || item?.priority || "").toLowerCase());
const getSourceType = (item) => SOURCE_KEYS.has(item?.sourceType)
    ? item.sourceType
    : item?.actor ? "user" : "system";
const getChangeType = (item) => {
    const candidates = [item?.eventKind, item?.action, item?.audit_log?.action]
        .map((value) => String(value || "").toLowerCase());
    return [...CHANGE_KEYS].find((key) => candidates.some((value) => value.includes(key))) || "";
};
const isInTimeRange = (value, range) => {
    if (range === "all") return true;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;
    const elapsed = Date.now() - date.getTime();
    const day = 86400000;
    if (range === "day") return elapsed >= 0 && elapsed <= day;
    if (range === "month") return elapsed >= 0 && elapsed <= 30 * day;
    return elapsed >= 0 && elapsed <= 7 * day;
};
const flattenEvent = (item) => {
    const values = [];
    const visit = (value, depth = 0) => {
        if (depth > 3 || value === null || value === undefined) return;
        if (["string", "number", "boolean"].includes(typeof value)) values.push(String(value));
        else if (Array.isArray(value)) value.forEach((child) => visit(child, depth + 1));
        else if (typeof value === "object") Object.values(value).forEach((child) => visit(child, depth + 1));
    };
    visit(item);
    return values.join(" ").toLocaleLowerCase();
};
const matchesEntities = (itemText, selected, options) => !selected.length || selected.some((selectedValue) => {
    const option = options.find((candidate) => candidate.value === selectedValue);
    return itemText.includes(String(selectedValue).toLocaleLowerCase())
        || (option?.label && itemText.includes(option.label.toLocaleLowerCase()));
});
const escapeCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
const getStatusMeta = (item) => {
    const changeType = getChangeType(item);
    if (changeType === "create") return {icon: "pi pi-plus", color: "bg-green-500"};
    if (changeType === "update") return {icon: "pi pi-pencil", color: "bg-blue-500"};
    if (changeType === "delete") return {icon: "pi pi-trash", color: "bg-red-500"};
    if (item?.eventCategory === "goal") return {icon: "pi pi-flag", color: "bg-cyan-500"};
    if (item?.eventCategory === "project") return {icon: "pi pi-folder", color: "bg-blue-500"};
    return {icon: "pi pi-bolt", color: "bg-gray-500"};
};
const getEventIdentifier = (item) => {
    const identifier = item?.eventCode || item?.code || item?.id;
    if (identifier !== null && identifier !== undefined) {
        return /^EVT-/i.test(String(identifier)) ? String(identifier) : `EVT-${identifier}`;
    }
    const documentIdentifier = item?.documentId;
    if (!documentIdentifier) return "";
    const shortIdentifier = String(documentIdentifier).replace(/-/g, "").slice(0, 4).toUpperCase();
    return shortIdentifier ? `EVT-${shortIdentifier}` : "";
};

function FilterControl({config, value, onChange, translate}) {
    const overlayRef = useRef(null);
    const [search, setSearch] = useState("");
    const selected = config.single ? (value === "all" ? [] : [value]) : value;
    const visibleOptions = config.options.filter((option) => option.label.toLocaleLowerCase()
        .includes(search.trim().toLocaleLowerCase()));
    const selectOption = (optionValue) => {
        if (config.single) {
            onChange(optionValue);
            return;
        }
        onChange(value.includes(optionValue)
            ? value.filter((current) => current !== optionValue)
            : [...value, optionValue]);
    };
    return (
        <div>
            <Button
                outlined
                size="small"
                className={`text-xs gap-2 white-space-nowrap border-round-lg ${selected.length ? "text-primary border-primary-200 surface-50" : "text-600 border-200"}`}
                onClick={(event) => overlayRef.current.toggle(event)}
            >
                <i className={`${config.icon} text-xs`}/>
                <span>{translate(config.label)}</span>
                {!!selected.length && <span className="inline-flex align-items-center justify-content-center border-circle bg-primary text-primary-contrast px-2 text-xs">{selected.length}</span>}
                <i className="pi pi-angle-down text-xs"/>
            </Button>
            <OverlayPanel
                ref={overlayRef}
                appendTo={document.body}
                className="text-xs"
                dismissable
                dir="rtl"
                pt={{content: {className: "p-3"}, root: {className: "border-round-xl shadow-4 z-5"}}}
            >
                <div className="flex flex-column gap-2 w-18rem" dir="rtl" onClick={(event) => event.stopPropagation()}>
                    <div className="flex justify-content-between align-items-start gap-2">
                        <strong className="text-xs text-900">{translate(config.title)}</strong>
                        <small className="text-500 text-xs">{translate(config.hint)}</small>
                    </div>
                    {config.searchable && (
                        <IconField className="flex align-items-center">
                            <InputIcon className="pi pi-search"/>
                            <InputText
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder={translate("events-center.filter.search")}
                                className="w-full text-xs border-round-lg py-2"
                            />
                        </IconField>
                    )}
                    <div className="flex flex-column gap-1 overflow-auto max-h-15rem">
                        {visibleOptions.length ? visibleOptions.map((option) => {
                            const checked = config.single ? value === option.value : value.includes(option.value);
                            return (
                                <label key={option.value} className="flex align-items-center gap-2 p-2 border-round-lg hover:surface-100 cursor-pointer">
                                    {config.single ? (
                                        <RadioButton inputId={`${config.id}-${option.value}`} checked={checked} value={option.value}
                                            onChange={() => selectOption(option.value)}/>
                                    ) : (
                                        <Checkbox inputId={`${config.id}-${option.value}`} checked={checked}
                                            onChange={() => selectOption(option.value)}/>
                                    )}
                                    <span className="text-xs text-700 flex-1">{option.label}</span>
                                </label>
                            );
                        }) : <span className="text-center text-500 text-xs p-3">{translate("events-center.filter.no-options")}</span>}
                    </div>
                    <div className="flex align-items-center justify-content-between gap-2 border-top-1 border-100 pt-2">
                        <Button label={translate(config.clearLabel)} text severity="secondary" size="small" className="text-xs"
                            onClick={() => onChange(config.single ? config.defaultValue : [])}/>
                        <Button label={translate("events-center.filter.done")} text size="small" className="text-xs"
                            onClick={() => overlayRef.current.hide()}/>
                    </div>
                </div>
            </OverlayPanel>
        </div>
    );
}

function EventsCenter() {
    const translate = useTranslate();
    const text = useCallback((key, values = {}) => Object.entries(values).reduce(
        (result, [name, value]) => result.replaceAll(`{{${name}}}`, value), translate(key)
    ), [translate]);
    const {projects, goals, teams, users, workspaceMemberships} = useSelector((state) => state.workspaceGraph);
    const currentUser = useSelector((state) => state.auth.user);
    const workspace = useSelector((state) => state.app.workspaceMembershipData?.workspace);
    const workspaceDocumentId = workspace?.documentId;
    const workspaceId = workspace?.id;
    const workspaceKey = workspaceDocumentId ?? workspaceId;
    const [auditLogs, setAuditLogs] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [search, setSearch] = useState("");
    const [viewMode, setViewMode] = useState("feed");
    const [expanded, setExpanded] = useState({});
    const [filters, setFilters] = useState({
        time: DEFAULT_TIME_RANGE, actors: [], categories: [], projects: [], goals: [], portfolios: [], teams: [],
        changes: [], severities: [], sources: [],
    });

    const refresh = useCallback(async () => {
        if (!workspaceKey) {
            setAuditLogs([]);
            setInitialLoading(false);
            return;
        }
        setInitialLoading(true);
        setLoadError(null);
        try {
            const auditResponse = await auditLogApi.getAll({
                filters: {workspace: workspaceDocumentId
                    ? {documentId: {$eq: workspaceDocumentId}}
                    : {id: {$eq: workspaceId}}},
                fields: ["model", "action", "before", "after", "patch", "performedById", "performedByType", "createdAt"],
                populate: {
                    actor: {
                        fields: ["documentId", "memberShipStatus"],
                        populate: {member: {fields: ["firstname", "lastname", "fullname"], populate: ["profileImage"]}},
                    },
                },
                sort: ["createdAt:desc"],
                pagination: {pageSize: 300},
            });
            setAuditLogs(auditResponse?.data || []);
        }
        catch (error) { setLoadError(error?.message || true); }
        finally { setInitialLoading(false); }
    }, [workspaceDocumentId, workspaceId, workspaceKey]);
    useEffect(() => { refresh(); }, [refresh]);

    const actorLookup = useMemo(() => {
        const values = new Map();
        const workspaceUsers = Array.isArray(users) ? users : Object.values(users || {});
        const membershipUsers = (workspaceMemberships || []).map((membership) => membership?.member).filter(Boolean);
        [...workspaceUsers, ...membershipUsers, currentUser].filter(Boolean).forEach((user) => {
            const key = valueKey(user);
            if (key !== null) values.set(String(key), user);
        });
        return values;
    }, [currentUser, users, workspaceMemberships]);
    const sourceItems = useMemo(() => auditLogs.flatMap((auditLog) => {
        const modelMeta = AUDITED_MODEL_META.get(auditLog?.model);
        if (!modelMeta) return [];

        const snapshot = auditLog.after || auditLog.before || {};
        const modelLabel = translate(modelMeta.labelKey);
        const subjectKey = relationTitle(snapshot) || snapshot.documentId || snapshot.id;
        const subjectLabel = subjectKey
            ? String(subjectKey)
            : translate("events-center.not-available");
        const actorId = auditLog.performedById;
        const actorProfile = auditLog?.actor?.member || (actorId === null || actorId === undefined
            ? null
            : actorLookup.get(String(actorId)) || null);
        const actor = actorProfile || (actorId === null || actorId === undefined ? null : {
            id: actorId,
            fullname: text("events-center.actor-id", {id: actorId}),
        });
        const action = CHANGE_KEYS.has(auditLog.action) ? auditLog.action : "update";

        return [{
            ...auditLog,
            actor,
            actorProfile,
            eventCategory: modelMeta.category,
            eventKind: action,
            sourceType: actorId === null || actorId === undefined ? "system" : "user",
            sourceLabel: modelLabel,
            contextLabel: subjectLabel,
            title: text(`events-center.audit-action.${action}`, {model: modelLabel, subject: subjectLabel}),
            priority: action === "delete" ? "important" : "normal",
        }];
    }), [actorLookup, auditLogs, text, translate]);
    const actorOptions = useMemo(() => {
        const actors = new Map();
        sourceItems.forEach((item) => {
            const key = valueKey(item.actor);
            if (key !== null) actors.set(String(key), {label: actorName(item.actor, translate("events-center.system-actor")), value: String(key)});
        });
        return Array.from(actors.values()).sort((left, right) => left.label.localeCompare(right.label));
    }, [sourceItems, translate]);
    const projectOptions = useMemo(() => (projects || []).filter(relationTitle).map(optionFromEntity), [projects]);
    const goalOptions = useMemo(() => (goals || []).filter(relationTitle).map(optionFromEntity), [goals]);
    const teamOptions = useMemo(() => (teams || []).filter(relationTitle).map(optionFromEntity), [teams]);
    const portfolioOptions = useMemo(() => {
        const values = new Map();
        (projects || []).flatMap((project) => project?.portfolios || []).forEach((portfolio) => {
            if (relationTitle(portfolio)) values.set(String(valueKey(portfolio)), optionFromEntity(portfolio));
        });
        return Array.from(values.values());
    }, [projects]);
    const categoryOptions = useMemo(() => ["project", "milestone", "goal", "task", "team", "workspace", "report"]
        .map((value) => ({value, label: translate(`events-center.category.${value}`)})), [translate]);
    const changeOptions = useMemo(() => [...CHANGE_KEYS]
        .map((value) => ({value, label: translate(`events-center.change.${value}`)})), [translate]);
    const severityOptions = useMemo(() => ["important", "normal"]
        .map((value) => ({value, label: translate(`events-center.importance.${value}`)})), [translate]);
    const sourceOptions = useMemo(() => [...SOURCE_KEYS]
        .map((value) => ({value, label: translate(`events-center.source.${value}`)})), [translate]);
    const timeOptions = useMemo(() => ["day", "week", "month", "all"]
        .map((value) => ({value, label: translate(`events-center.time.${value}`)})), [translate]);
    const viewOptions = useMemo(() => [
        {label: translate("events-center.feed-view"), value: "feed"},
        {label: translate("events-center.table-view"), value: "table"},
    ], [translate]);

    const filterConfigs = [
        {id: "time", label: "events-center.filter.time", title: "events-center.filter.time-title", hint: "events-center.filter.single-hint", icon: "pi pi-calendar", options: timeOptions, single: true, defaultValue: "all", clearLabel: "events-center.filter.default"},
        {id: "actors", label: "events-center.filter.actors", title: "events-center.filter.actors-title", hint: "events-center.filter.multi-hint", icon: "pi pi-users", options: actorOptions, searchable: true, clearLabel: "events-center.filter.clear-actors"},
        {id: "categories", label: "events-center.filter.categories", title: "events-center.filter.categories-title", hint: "events-center.filter.multi-hint", icon: "pi pi-bars", options: categoryOptions, clearLabel: "events-center.filter.clear-categories"},
        {id: "projects", label: "events-center.filter.projects", title: "events-center.filter.projects-title", hint: "events-center.filter.multi-hint", icon: "pi pi-briefcase", options: projectOptions, searchable: true, clearLabel: "events-center.filter.clear-projects"},
        {id: "goals", label: "events-center.filter.goals", title: "events-center.filter.goals-title", hint: "events-center.filter.multi-hint", icon: "pi pi-bullseye", options: goalOptions, searchable: true, clearLabel: "events-center.filter.clear-goals"},
        {id: "portfolios", label: "events-center.filter.portfolios", title: "events-center.filter.portfolios-title", hint: "events-center.filter.multi-hint", icon: "pi pi-th-large", options: portfolioOptions, searchable: true, clearLabel: "events-center.filter.clear-portfolios"},
        {id: "teams", label: "events-center.filter.teams", title: "events-center.filter.teams-title", hint: "events-center.filter.multi-hint", icon: "pi pi-users", options: teamOptions, searchable: true, clearLabel: "events-center.filter.clear-teams"},
        {id: "changes", label: "events-center.filter.changes", title: "events-center.filter.changes-title", hint: "events-center.filter.multi-hint", icon: "pi pi-check-square", options: changeOptions, clearLabel: "events-center.filter.clear-changes"},
        {id: "severities", label: "events-center.filter.importance", title: "events-center.filter.importance-title", hint: "events-center.filter.multi-hint", icon: "pi pi-exclamation-circle", options: severityOptions, clearLabel: "events-center.filter.clear-importance"},
        {id: "sources", label: "events-center.filter.source", title: "events-center.filter.source-title", hint: "events-center.filter.multi-hint", icon: "pi pi-sun", options: sourceOptions, clearLabel: "events-center.filter.clear-source"},
    ];
    const filteredItems = useMemo(() => sourceItems.filter((item) => {
        const itemText = flattenEvent(item);
        const needle = search.trim().toLocaleLowerCase();
        const severity = isImportant(item) ? "important" : "normal";
        return (!needle || itemText.includes(needle))
            && isInTimeRange(item.createdAt, filters.time)
            && (!filters.actors.length || filters.actors.includes(String(valueKey(item.actor))))
            && (!filters.categories.length || filters.categories.includes(item.eventCategory || "system"))
            && matchesEntities(itemText, filters.projects, projectOptions)
            && matchesEntities(itemText, filters.goals, goalOptions)
            && matchesEntities(itemText, filters.portfolios, portfolioOptions)
            && matchesEntities(itemText, filters.teams, teamOptions)
            && (!filters.changes.length || filters.changes.includes(getChangeType(item)))
            && (!filters.severities.length || filters.severities.includes(severity))
            && (!filters.sources.length || filters.sources.includes(getSourceType(item)));
    }).sort((left, right) => new Date(right?.createdAt || 0) - new Date(left?.createdAt || 0)),
    [filters, goalOptions, portfolioOptions, projectOptions, search, sourceItems, teamOptions]);
    const groupedItems = useMemo(() => {
        const groups = new Map();
        filteredItems.forEach((item) => {
            const date = new Date(item.createdAt);
            const key = Number.isNaN(date.getTime()) ? "unknown" : date.toISOString().slice(0, 10);
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(item);
        });
        return Array.from(groups.entries());
    }, [filteredItems]);
    const activeFilterCount = Object.entries(filters).reduce((count, [key, value]) => count
        + (key === "time" ? (value === "all" ? 0 : 1) : value.length), 0);
    const importantCount = sourceItems.filter((item) => {
        const elapsed = Date.now() - new Date(item.createdAt).getTime();
        return isImportant(item) && elapsed >= 0 && elapsed <= 2 * 86400000;
    }).length;
    const clearFilters = () => setFilters({time: "all", actors: [], categories: [], projects: [], goals: [], portfolios: [], teams: [], changes: [], severities: [], sources: []});
    const removeFilterValue = (filterId, optionValue) => setFilters((current) => ({
        ...current,
        [filterId]: filterId === "time"
            ? "all"
            : current[filterId].filter((value) => value !== optionValue),
    }));
    const activeChips = filterConfigs.flatMap((config) => {
        const values = config.single
            ? (filters[config.id] === "all" ? [] : [filters[config.id]])
            : filters[config.id];
        return values.map((value) => ({
            id: config.id,
            value,
            label: translate(config.label),
            valueLabel: config.options.find((option) => option.value === value)?.label || value,
        }));
    });
    const formatDate = (value, format = "YYYY/MM/DD HH:mm") => {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return translate("events-center.unknown-date");
        return dateToPersian(date, format);
    };
    const exportCsv = () => {
        const rows = filteredItems.map((item) => [formatDate(item.createdAt), actorName(item.actor, translate("events-center.system-actor")),
            item.eventCategory, item.title || item.eventKind || "", item.message || "", item.sourceLabel || "", item.contextLabel || ""]);
        const csv = [["date", "actor", "category", "title", "message", "source", "context"], ...rows]
            .map((row) => row.map(escapeCsv).join(",")).join("\n");
        const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], {type: "text/csv;charset=utf-8"}));
        const anchor = document.createElement("a");
        anchor.href = url; anchor.download = `zofino-events-${new Date().toISOString().slice(0, 10)}.csv`; anchor.click();
        URL.revokeObjectURL(url);
    };
    const categoryLabel = (item) => translate(`events-center.category.${CATEGORY_KEYS.has(item?.eventCategory) ? item.eventCategory : "system"}`);
    const auditFieldLabel = (item, field) => {
        const translationKey = `events-center.audit-field.${field}`;
        const translated = translate(translationKey);
        const baseLabel = translated === translationKey ? translate("events-center.audit-field.unknown") : translated;
        const subjectLabel = item?.after?.fullname
            || item?.before?.fullname
            || item?.contextLabel;
        return field === "role" && subjectLabel ? `${baseLabel} ${subjectLabel}` : baseLabel;
    };
    const auditValue = (value) => formatAuditValue(value, translate, (date) => dateToPersian(date, "YYYY/MM/DD - HH:mm"));

    return (
        <div className="h-full overflow-auto surface-ground" dir="rtl">
            <main className="mx-auto p-3 md:p-5 lg:p-6 flex flex-column gap-4 max-w-screen-xl">
                <section className="flex flex-column-reverse md:flex-row-reverse align-items-start justify-content-between gap-3">
                    <div className="flex flex-wrap gap-2">
                        <Button label={translate("events-center.refresh")} icon="pi pi-refresh" outlined size="small"
                                className="text-xs gap-2 border-round-lg" loading={initialLoading} onClick={refresh}/>
                        <Button label={translate("events-center.export-excel")} icon="pi pi-file-excel" outlined size="small"
                            className="text-xs gap-2 border-round-lg" disabled={!filteredItems.length} onClick={exportCsv}/>

                    </div>
                    <div className="text-right">
                        <span className="inline-flex align-items-center gap-2 text-primary text-xs font-bold mb-2">
                            <span className="w-1rem h-1rem border-circle bg-primary"/> {translate("events-center")}
                        </span>
                        <h1 className="text-3xl md:text-5xl text-900 mt-0 mb-2 line-height-2">{translate("events-center.heading")}</h1>
                        <p className="text-sm text-600 m-0">{translate("events-center.description")}</p>
                    </div>
                </section>

                <section className="surface-card border-1 border-orange-200 border-round-xl p-3 md:p-4 flex flex-wrap align-items-center gap-3">
                    <div className="w-3rem h-3rem border-round-xl bg-orange-50 text-primary flex align-items-center justify-content-center"><i className="pi pi-exclamation-circle text-xl"/></div>
                    <div className="flex-1 min-w-0">
                        <strong className="block text-sm text-900 mb-1">{text("events-center.summary-title", {count: importantCount})}</strong>
                        <span className="text-xs text-500">{text("events-center.summary-description", {count: sourceItems.length})}</span>
                    </div>
                    <Button label={translate("events-center.only-important-full")} outlined size="small"
                        className={`text-xs gap-2 border-round-lg ${filters.severities.includes("important") ? "bg-orange-50 text-primary border-orange-200" : ""}`}
                        onClick={() => setFilters((current) => ({...current, severities: current.severities.includes("important") ? [] : ["important"]}))}/>
                </section>

                <section className="surface-card border-1 border-200 border-round-xl shadow-1 p-3 md:p-4 flex flex-column gap-3">
                    <IconField className="flex align-items-center"><InputIcon className="pi pi-search"/>
                        <InputText value={search} onChange={(event) => setSearch(event.target.value)}
                            placeholder={translate("events-center.search-placeholder")} className="w-full text-xs border-round-xl py-3"/>
                    </IconField>
                    <div className="border-top-1 border-100 pt-3">
                        <div className="flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                            <div><strong className="text-xs text-900 ">{translate("events-center.filter-title")}</strong>
                                <span className="text-xs text-500 mr-2">{translate("events-center.filter-description")}</span></div>
                            <Button label={translate("events-center.clear-all")} text severity="danger" size="small" className="text-xs"
                                disabled={activeFilterCount === 0 && !search} onClick={() => { clearFilters(); setSearch(""); }}/>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {filterConfigs.map((config) => <FilterControl key={config.id} config={config} value={filters[config.id]}
                                onChange={(value) => setFilters((current) => ({...current, [config.id]: value}))}
                                translate={translate}/>) }
                        </div>
                    </div>
                    <div className="w-full border-top-1  border-200 mt-3 py-3 flex flex-wrap align-items-center justify-content-start gap-2 min-h-1rem">
                        {activeChips.map((chip) => (
                            <span
                                key={`${chip.id}-${chip.value}`}
                                className="inline-flex align-items-center gap-2 surface-100 border-1 border-200 border-round-xl px-3 py-1 text-xs text-700 line-height-1"
                            >
      <span className="white-space-nowrap">
        {chip.label}: <span className="font-semibold">{chip.valueLabel}</span>
      </span>

      <Button
          icon="pi pi-times"
          text
          rounded
          severity="secondary"
          size="small"
          aria-label={translate("events-center.remove-filter")}
          className="p-0 flex-shrink-0 text-500"
          style={{width: "1.25rem", height: "1.25rem"}}
          onClick={() => removeFilterValue(chip.id, chip.value)}/>
    </span>
                        ))}
                    </div>

                </section>

                {loadError && <Message severity="error" text={loadError === true ? translate("events-center.load-error") : loadError} className="text-xs"/>}
                <div className="flex flex-wrap align-items-center justify-content-between gap-2">
                    <div><strong className="text-xl text-900">{translate("events-center.latest")}</strong>
                        <small className="text-xs text-500 mr-2">{text("events-center.result-count", {count: filteredItems.length})}</small></div>
                    <SelectButton
                        value={viewMode}
                        options={viewOptions}
                        optionLabel="label"
                        optionValue="value"
                        allowEmpty={false}
                        onChange={(event) => event.value && setViewMode(event.value)}
                        className="text-xs surface-100 border-round-xl p-1"
                        pt={{
                            button: ({context}) => ({
                                className: `text-xs px-3 py-2 border-none border-round-lg transition-colors transition-duration-150 ${context.selected ? "surface-card text-900 shadow-1 font-bold" : "surface-100 text-500 font-normal"}`,
                            }),
                        }}
                    />
                </div>

                {initialLoading ? <div className="flex justify-content-center p-6"><ProgressSpinner/></div>
                    : !filteredItems.length ? <div className="surface-card border-1 border-200 border-round-xl flex flex-column align-items-center text-center p-6 text-500">
                        <i className="pi pi-calendar-times text-4xl text-300 mb-3"/><strong className="text-sm text-700">{translate("events-center.empty-title")}</strong>
                        <span className="text-xs mt-2">{translate("events-center.empty-description")}</span></div>
                    : viewMode === "table" ? <DataTable value={filteredItems} paginator rows={15} responsiveLayout="scroll"
                        className="text-xs surface-card border-round-xl overflow-hidden">
                        <Column className="text-right" header={translate("events-center.column.date")} body={(item) => formatDate(item.createdAt)}/>
                        <Column className="text-right" header={translate("events-center.column.actor")} body={(item) => actorName(item.actor, translate("events-center.system-actor"))}/>
                        <Column className="text-right" header={translate("events-center.column.event")} body={(item) => translate(item.title || item.eventKind || item.message)}/>
                        <Column className="text-right" header={translate("events-center.column.category")} body={(item) => categoryLabel(item)}/>
                        <Column className="text-right" header={translate("events-center.column.importance")} body={(item) => <Tag
                            value={translate(isImportant(item) ? "events-center.importance.important" : "events-center.importance.normal")}
                            severity={isImportant(item) ? "warning" : "secondary"} rounded/>}/>
                    </DataTable> : groupedItems.map(([dateKey, items]) => <section key={dateKey} className="flex flex-column gap-2">
                        <div className="text-xs text-500 font-bold px-2 flex align-items-center gap-2"><span className="w-1rem h-1rem border-circle bg-cyan-500"/>{dateKey === "unknown" ? translate("events-center.unknown-date") : formatDate(dateKey, "dddd D MMMM")}</div>
                        {items.map((item) => {
                            const key = valueKey(item);
                            const isExpanded = !!expanded[key];
                            const changes = getAuditChanges(item);
                            const statusMeta = getStatusMeta(item);
                            const changeType = getChangeType(item);
                            const eventIdentifier = getEventIdentifier(item);
                            return <article key={key} className={`surface-card border-1 border-round-xl overflow-hidden ${isImportant(item) ? "border-orange-200" : "border-200"}`}>
                                <div className="flex flex-column md:flex-row align-items-start gap-3 p-3 md:p-4">
                                    <div className="relative w-4rem h-4rem flex-shrink-0 flex align-items-center justify-content-center">
                                        <div className="w-3rem h-3rem border-circle bg-primary-50 text-primary flex align-items-center justify-content-center">{item.actorProfile
                                            ? <Avatar size="large" memberData={item.actorProfile}/>
                                            : <i className={`pi ${item.actor ? "pi-user" : "pi-bolt"}`}/>}</div>
                                        <span className={`absolute bottom-0 left-0 p-1 border-circle border-2 border-white text-white flex align-items-center justify-content-center ${statusMeta.color}`}>
                                            <i className={`${statusMeta.icon} text-xs`}/>
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm md:text-base font-bold text-900 line-height-3">{translate(item.title || item.eventKind || "events-center.event-fallback")}</div>
                                        <div className="flex flex-wrap align-items-center gap-2 text-xs text-500 mt-2">
                                            {(item.sourceLabel || item.contextLabel) && <span>{[item.sourceLabel, item.contextLabel].filter(Boolean).join(" / ")}</span>}
                                            <i className="pi pi-circle-fill text-300 text-xs"/><span>{formatDate(item.createdAt)}</span><i className="pi pi-circle-fill text-300 text-xs"/>
                                            <Tag value={categoryLabel(item)} severity="secondary" rounded
                                                className="surface-100 text-600 text-2xs font-normal"/>
                                            {changeType && <Tag value={translate(`events-center.change.${changeType}`)} severity="secondary" rounded
                                                className="surface-100 text-600 text-2xs font-normal"/>}
                                            <Tag value={translate(isImportant(item) ? "events-center.importance.important" : "events-center.importance.normal")}
                                                severity="secondary" rounded className="surface-100 text-600 text-2xs font-normal"/>
                                        </div>
                                    </div>
                                    <Button label={translate(isExpanded ? "events-center.hide-details" : "events-center.show-details")}
                                        icon={`pi pi-chevron-${isExpanded ? "up" : "down"}`} iconPos="right" text severity="secondary" size="small"
                                        className="text-xs gap-2 flex-shrink-0"
                                        onClick={() => setExpanded((current) => ({...current, [key]: !current[key]}))}/>
                                </div>
                                {isExpanded && <div className="surface-50 border-top-1 border-dashed border-200 p-3 md:px-5 md:pb-4">
                                    <strong className="block text-xs text-900 mb-3">{translate("events-center.change-title")}</strong>
                                    {changes.length ? <div className="flex flex-column gap-2">{changes.map(({field, before, after}) => <div key={field} className="grid align-items-stretch">
                                        <div className="col-12 md:col-3"><div className="surface-100 border-1 border-200 border-round-lg p-3 text-xs text-600 h-full flex align-items-center justify-content-center text-right">{auditFieldLabel(item, field)}</div></div>
                                        <div className="col-12 md:col-4"><div className="surface-card border-1 border-200 border-round-lg p-3 text-xs text-600 h-full flex flex-column gap-2 text-right"><small className="text-500">{translate("events-center.before-value")}</small><span>{before === null || before === undefined ? translate("events-center.previous-unknown") : auditValue(before)}</span></div></div>
                                        <div className="col-12 md:col-1 flex align-items-center justify-content-center text-500"><i className="pi pi-arrow-left"/></div>
                                        <div className="col-12 md:col-4"><div className={`${isImportant(item) ? "bg-orange-50 border-orange-100 text-orange-700" : "bg-green-50 border-green-100 text-green-700"} border-1 border-round-lg p-3 text-xs font-bold h-full flex flex-column gap-2 text-right`}><small>{translate("events-center.after-value")}</small><span>{auditValue(after)}</span></div></div>
                                    </div>)}</div> : <div className="surface-card border-1 border-200 border-round-lg p-3 text-xs text-500">{translate("events-center.no-change-details")}</div>}
                                    {item.message && <div className="flex align-items-start gap-2 surface-100 text-700 border-round-lg p-3 mt-3 text-xs line-height-3">
                                        <i className="pi pi-info-circle text-primary mt-1"/><span><strong>{translate("events-center.change-impact")}: </strong>{translate(item.message)}</span>
                                    </div>}
                                    <div className="flex flex-wrap align-items-center gap-1 mt-3">
                                        {eventIdentifier && <Button label={`${translate("events-center.event-id")}: ${eventIdentifier}`} text severity="secondary" size="small"
                                            className="text-xs font-bold" onClick={() => navigator.clipboard?.writeText(eventIdentifier)}/>} 
                                    </div>
                                </div>}
                            </article>;
                        })}
                    </section>)}
            </main>
        </div>
    );
}

export default EventsCenter;
